import ColorHash from "color-hash";

const colorHash = new ColorHash();

/**
 * The least lightness a hashed color has in dark mode. The hash's own
 * lightness is one of 35%, 50% or 65%, and a 35% dot all but vanishes on a
 * dark background.
 */
const DARK_MODE_MIN_LIGHTNESS = 0.6;

const percent = (fraction: number) => `${Math.round(fraction * 100)}%`;

/**
 * The color of a tag's dot: a stable hash of its label, so a tag reads the
 * same everywhere it appears, except that labels naming an error, a success
 * or a warning take the palette's red, green or yellow, the solid step the
 * status badges' tints are drawn from, so they follow the theme. Case
 * doesn't matter: "Rain" and "rain" are one color.
 *
 * In light mode a hashed color is exactly the one the label has always had.
 * In dark mode it keeps its hue but is lifted to a lightness that shows on a
 * dark background, through `light-dark()` as the palette's own tokens are.
 */
export function tagColor(label: string): string {
  const lower = label.toLowerCase();
  if (lower.includes("error") || lower.includes("fail")) {
    return "var(--red-9)";
  }
  if (lower.includes("success") || lower.includes("pass")) {
    return "var(--green-9)";
  }
  if (lower.includes("warn")) {
    return "var(--yellow-9)";
  }
  const [r, g, b] = colorHash.rgb(lower);
  const [hue, saturation, lightness] = colorHash.hsl(lower);
  const lifted = Math.max(lightness, DARK_MODE_MIN_LIGHTNESS);
  const dark = `hsl(${hue} ${percent(saturation)} ${percent(lifted)})`;
  return `light-dark(rgb(${r}, ${g}, ${b}), ${dark})`;
}
